import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./lib/auth-context.js";
import { Layout } from "./components/Layout.js";
import { RequireAuth } from "./components/RequireAuth.js";
import { HomePage } from "./pages/HomePage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { RegisterPage } from "./pages/RegisterPage.js";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage.js";
import { ResetPasswordPage } from "./pages/ResetPasswordPage.js";
import { ClaimsListPage } from "./pages/ClaimsListPage.js";
import { ClaimReportPage } from "./pages/ClaimReportPage.js";
import { CreateClaimPage } from "./pages/CreateClaimPage.js";
import { ClaimVersionsPage } from "./pages/ClaimVersionsPage.js";
import { NewVersionPage } from "./pages/NewVersionPage.js";
import { AccountPage } from "./pages/AccountPage.js";
import { AdminPage } from "./pages/AdminPage.js";
import { OrgAdminPage } from "./pages/OrgAdminPage.js";
import { ReleasesPage } from "./pages/ReleasesPage.js";
import { FeedbackTriagePage } from "./pages/FeedbackTriagePage.js";
import { EdufadsPage } from "./pages/EdufadsPage.js";
import { EdufadReportPage } from "./pages/EdufadReportPage.js";
import { CheckPage } from "./pages/CheckPage.js";
import { ResearchPage } from "./pages/ResearchPage.js";
import { GetExtensionPage } from "./pages/GetExtensionPage.js";
import { GuidePage } from "./pages/GuidePage.js";
import { PracticePage } from "./pages/PracticePage.js";
import { AboutPage } from "./pages/AboutPage.js";
import { PrivacyPage } from "./pages/PrivacyPage.js";
import { SuggestPage } from "./pages/SuggestPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { SuggestionsPage } from "./pages/SuggestionsPage.js";

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Public — no account needed. */}
            <Route path="/edufads" element={<EdufadsPage />} />
            <Route path="/edufads/:claimId" element={<EdufadReportPage />} />
            <Route path="/check" element={<CheckPage />} />
            <Route path="/research" element={<ResearchPage />} />
            <Route path="/get-extension" element={<GetExtensionPage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/practice" element={<PracticePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/suggest" element={<SuggestPage />} />

            <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
            <Route path="/suggestions" element={<RequireAuth permission="feedback:triage"><SuggestionsPage /></RequireAuth>} />

            <Route path="/claims" element={<RequireAuth permission="claims:read"><ClaimsListPage /></RequireAuth>} />
            <Route path="/claims/new" element={<RequireAuth permission="claims:write"><CreateClaimPage /></RequireAuth>} />
            <Route path="/claims/:claimId" element={<RequireAuth permission="claims:read"><ClaimReportPage /></RequireAuth>} />
            <Route
              path="/claims/:claimId/versions"
              element={<RequireAuth permission="claims:write"><ClaimVersionsPage /></RequireAuth>}
            />
            <Route
              path="/claims/:claimId/versions/new"
              element={<RequireAuth permission="claims:write"><NewVersionPage /></RequireAuth>}
            />

            <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
            <Route path="/admin" element={<RequireAuth permission="admin:users:manage"><AdminPage /></RequireAuth>} />
            {/* No blanket permission check here on purpose: org-level access is
                per-org (via me.orgs / membership role), not a platform-wide
                permission string, so it can't be checked generically the way
                "claims:write" can. The page itself calls the real API, which
                enforces real per-org authorization, and shows its own error
                state if that 403s. */}
            <Route path="/orgs/:orgId" element={<RequireAuth><OrgAdminPage /></RequireAuth>} />
            <Route path="/releases" element={<RequireAuth permission="claims:read"><ReleasesPage /></RequireAuth>} />
            <Route path="/feedback" element={<RequireAuth permission="feedback:triage"><FeedbackTriagePage /></RequireAuth>} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
